import { Navigate, Outlet } from "react-router-dom";

import { selectIsAuth } from "@/features/auth/authSlice";
import { useAppSelector } from "@/hooks/redux";

/**
 * Hook to redirect based on authentication state.
 *
 * @param {React.ReactNode} authenticatedComponent - The component to render if authenticated.
 * @returns {React.ReactNode} The authenticatedComponent if authenticated, or a Navigate component to /login if not authenticated.
 */
const useAuthRedirect = (authenticatedComponent: React.ReactNode): React.ReactNode => {
  const isAuth = useAppSelector(selectIsAuth);
  return isAuth ? authenticatedComponent : <Navigate to="/login" replace />;
};

/**
 * Component to handle redirection to default route based on authentication state.
 *
 * @returns {React.ReactNode} A Navigate component to /chat if authenticated, or to /login if not authenticated.
 */
const RedirectToDefault = (): React.ReactNode => {
  return useAuthRedirect(<Navigate to="/chat" replace />);
};

/**
 * Layout component to protect routes based on authentication state.
 *
 * @returns {React.ReactNode} An Outlet component if authenticated, or a Navigate component to /login if not authenticated.
 */
const AuthLayout = (): React.ReactNode => {
  return useAuthRedirect(<Outlet />);
};

export { RedirectToDefault, AuthLayout };